import { Button, Grid, Stack, FormControl, OutlinedInput, InputAdornment, Typography, Modal, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { IconEye, IconPlus } from '@tabler/icons';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainCard from 'ui-component/cards/MainCard';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { get, del } from '../../axios/apiService';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { Box, textAlign } from '@mui/system';
import toast from 'react-hot-toast';
import { Chip } from "@mui/material";
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';
import { Visibility } from '@mui/icons-material';

const Blog = () => {
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(false);
    const [blogs, setBlogs] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [open, setOpen] = useState(false);
    const [isDeleteBlogId, setIsDeleteBlogId] = useState('');
    const [sortModel, setSortModel] = useState([]);
    const [search, setSearch] = useState('');
    const { hasWritePermission } = usePermissions();
    const canEdit = hasWritePermission(MODULE_KEYS.Blogs);

    // Fetch blogs
    useEffect(() => {
        const fetchBlogs = async () => {
            setLoading(true);
            try {
                const sortField = sortModel.length > 0 ? sortBlogField(sortModel[0].field) : 'CreatedDate';
                const sortOrder = sortModel.length > 0 ? (sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending') : 'Descending';
                const sortQuery = `&$orderby=${sortField}&$orderbydirection=${sortOrder}`;
                const searchQuery = search != '' ? `&$filter=Title~LIKE~${search} OR MetaKeyWords~LIKE~${search}` : '';

                let response = await get(
                    `Blog?$skip=${paginationModel.page * paginationModel.pageSize}&$top=${paginationModel.pageSize}${searchQuery}${sortQuery}`
                );

                let transformedBlogs = [];
                if (response && Array.isArray(response.values)) {
                    transformedBlogs = response.values.map((blog) => ({
                        id: blog.id || '',
                        title: blog.title || '',
                        metaKeyWords: blog.metaKeyWords || '',
                        status: blog.isPublished || false,
                        createdDate: blog.createdDate || null
                    }));

                    setBlogs(transformedBlogs);
                    setRowCount(response.totalCount || 0);
                } else {
                    setBlogs([]);
                    setRowCount(0);
                }
            } catch (err) {
                console.error(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs();
    }, [paginationModel, sortModel, search]);

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleSortModelChange = (model) => {
        setSortModel(model);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    // Action buttons
    const ActionButtons = (row) => (
        <Stack direction={'row'} spacing={1}>
            <IconButton color="primary" onClick={() => navigate(`/blog/edit-blog/${row.id}`)}>
                {
                    canEdit ? <EditIcon /> : <Visibility />
                }
            </IconButton>
            {
                canEdit && (
                    <IconButton
                        color="error"
                        onClick={() => {
                            setOpen(true);
                            setIsDeleteBlogId(row.id);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                )
            }
        </Stack>
    );

    const handleClose = () => {
        setOpen(false);
        setIsDeleteBlogId('');
    };

    const DeleteBlog = async () => {
        if (!isDeleteBlogId) return setOpen(false);
        setLoading(true);
        try {
            let response = await del(`Blog/${isDeleteBlogId}`, null);
            if (!response) throw new Error(`HTTP error! status: ${response.status}`);
            toast.success('Blog deleted successfully');
            handleClose();
            setPaginationModel({ page: 0, pageSize: 10 });
        } catch (error) {
            toast.error('Failed to delete blog');
        } finally {
            setLoading(false);
        }
    };

    // Grid columns for blog table
    const columns = [
        { field: 'title', headerName: 'Title', flex: 1 },
        { field: 'metaKeyWords', headerName: 'Meta KeyWords', flex: 1 },
        {
            field: 'CreatedDate',
            headerName: 'Created Date',
            flex: 1,
            renderCell: (params) => (params.row.createdDate ? dayjs(params.row.createdDate).format('DD-MMM-YYYY') : '')
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) =>
                params.row.status ? (<Chip label="Publish" color="success" size="small" />) : (
                    <Chip label="Draft" color="error" size="small" />)
        },
        {
            field: 'action',
            headerName: 'Actions',
            renderCell: (params) => <ActionButtons {...params.row} />,
            align: 'right',
            width: 180,
            sortable: false,
            filterable: false
        }
    ];

    const handeRedirectAddblog = () => navigate('/blog/add-blog');

    return (
        <>
            <MainCard title="Blog Management">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0}>
                            <form noValidate autoComplete="off" className='formSearch'>
                                <FormControl sx={{ width: '25ch' }} size="small">
                                    <OutlinedInput
                                        placeholder="Search"
                                        value={search}
                                        onChange={handleSearchChange}
                                        endAdornment={
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            </form>
                            {
                                canEdit && (
                                    <>
                                        <Button variant="contained" color="primary" onClick={handeRedirectAddblog} startIcon={<IconPlus />}>
                                            Add
                                        </Button>
                                    </>
                                )
                            }
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <DataGrid
                            getRowId={(row) => row.id}
                            rows={blogs}
                            rowCount={rowCount}
                            columns={columns}
                            paginationMode="server"
                            sortingMode="server"
                            pageSizeOptions={[10]}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            components={{ NoRowsOverlay: CustomNoRowsOverlay }}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            autoHeight={true}
                        />
                    </Grid>
                </Grid>
            </MainCard>

            {/* Delete Confirmation Modal */}
            <Modal open={open} onClose={handleClose}>
                <Box className="modalBox">
                    <Typography sx={{ fontSize: '20px' }}>Delete Confirmation</Typography>
                    <Typography sx={{ mt: 2 }}>Are you sure you want to delete this Blog?</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                        <Button onClick={handleClose} variant="contained" color="error" sx={{ mt: 2, mr: 2 }}>
                            Cancel
                        </Button>
                        <Button variant="contained" sx={{ mt: 2 }} onClick={DeleteBlog}>
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <LoadingBackdrop open={isLoading} />
        </>
    );
};

export default Blog;

// Sorting helper
const sortBlogField = (field) => {
    if (field === 'title') return 'Title';
    if (field === 'metaKeyWords') return 'MetaKeyWords';
    if (field === 'createdDate') return 'CreatedDate';
    return 'Title';
};
